import React, { Fragment } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import {
  Shield,
  Home,
  Menu,
  Activity,
  Repeat,
  Building2,
  Database,
  GitBranch,
  Table2,
  BookOpen,
  ScrollText,
  Zap,
  ShieldCheck,
  Settings,
  ChevronsLeft,
  X,
  LogOut,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useHasFeature } from '../../hooks/useEntitlements';
import { useChatLayout } from '../layout/ChatLayout';
import { clsx } from 'clsx';
import zaminoLogo from '@/assets/zamino-logo.png';

// Final nav per the data-platform redesign (spec §0): pure navigation — no
// New Chat / Recent / workspace switcher (D13, D12). Dashboards is unlinked
// for now (route still exists — D26).
interface NavItem {
  icon: LucideIcon;
  label: string;
  href: string;
  /** Restrict to internal testers (users.is_internal_tester) while pre-GA. */
  internalOnly?: boolean;
  /** Require a tier entitlement (e.g. Pulse is Scale-only). */
  requiresFeature?: 'pulse';
  /** Restrict to Zamino staff (users.platform_role). */
  platformAdminOnly?: boolean;
}

const navItems: NavItem[] = [
  { icon: Home, label: 'Home', href: '/home' },
  { icon: Activity, label: 'Pulse', href: '/pulse', requiresFeature: 'pulse' },
  { icon: Repeat, label: 'Routines', href: '/routines', internalOnly: true },
  { icon: Building2, label: 'Businesses', href: '/businesses' },
  { icon: Database, label: 'Data sources', href: '/sources' },
  { icon: GitBranch, label: 'Relationships', href: '/sources/relationships', internalOnly: true },
  { icon: Table2, label: 'Data viewer', href: '/data-viewer' },
  { icon: BookOpen, label: 'Semantics', href: '/semantics' },
  { icon: ScrollText, label: 'Query log', href: '/query-log' },
  { icon: Zap, label: 'Dynamic analysis', href: '/dynamic-analysis', internalOnly: true },
  { icon: ShieldCheck, label: 'Admin', href: '/admin/users', platformAdminOnly: true },
];

function visibleNavItems(opts: {
  isInternalTester?: boolean;
  hasPulse: boolean;
  isPlatformAdmin: boolean;
}): NavItem[] {
  return navItems.filter((item) => {
    if (item.internalOnly && !opts.isInternalTester) return false;
    if (item.requiresFeature === 'pulse' && !opts.hasPulse) return false;
    if (item.platformAdminOnly && !opts.isPlatformAdmin) return false;
    return true;
  });
}

// Returns the href of the single nav item to render active for `pathname`,
// picking the longest matching prefix so a specific child (e.g.
// /sources/relationships) wins over its parent (/sources) instead of both
// lighting up. The Wiki page has no nav entry (spec §0) — it lights up Businesses.
function activeNavHref(pathname: string, items: NavItem[]): string | null {
  if (pathname.startsWith('/wiki')) return '/businesses';
  let best: string | null = null;
  for (const { href } of items) {
    if (pathname === href || pathname.startsWith(`${href}/`)) {
      if (best === null || href.length > best.length) best = href;
    }
  }
  return best;
}

// ── Collapsed sidebar (icon strip) ──────────────────────────────────────

const CollapsedSidebar: React.FC = () => {
  const { toggleSidebar } = useChatLayout();
  const navigate = useNavigate();
  const { user, logout, isPlatformAdmin } = useAuth();
  const hasPulse = useHasFeature('pulse');
  const location = useLocation();

  const navItemsToShow = visibleNavItems({
    isInternalTester: user?.isInternalTester,
    hasPulse,
    isPlatformAdmin,
  });
  const activeHref = activeNavHref(location.pathname, navItemsToShow);

  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : '?';

  const iconBtn =
    'flex items-center justify-center w-10 h-10 rounded-lg transition-colors';
  const defaultStyle =
    'text-muted-foreground hover:bg-accent';
  const activeStyle =
    'bg-accent text-primary';

  return (
    <>
      {/* Logo */}
      <button
        onClick={() => navigate('/')}
        className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary mb-1 hover:bg-primary/90 transition-colors cursor-pointer"
        title="Go to home page"
      >
        <Shield className="w-5 h-5 text-primary-foreground" />
      </button>

      {/* Menu toggle */}
      <button
        onClick={toggleSidebar}
        className={clsx(iconBtn, defaultStyle)}
        title="Expand sidebar (Ctrl+B)"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Divider */}
      <div className="w-8 border-t border-border my-1" />

      {/* Nav icons */}
      {navItemsToShow.map(({ icon: Icon, label, href }) => {
        const isActive = href === activeHref;
        return (
          <NavLink
            key={href}
            to={href}
            className={clsx(iconBtn, isActive ? activeStyle : defaultStyle)}
            title={label}
          >
            <Icon className="w-5 h-5" />
          </NavLink>
        );
      })}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Settings */}
      <NavLink
        to="/settings"
        className={clsx(
          iconBtn,
          location.pathname.startsWith('/settings') ? activeStyle : defaultStyle
        )}
        title="Settings"
      >
        <Settings className="w-5 h-5" />
      </NavLink>

      {/* Sign out */}
      <button
        onClick={logout}
        className={clsx(iconBtn, 'text-destructive hover:bg-destructive/15')}
        title="Sign out"
      >
        <LogOut className="w-5 h-5" />
      </button>

      {/* User avatar */}
      <div
        className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground text-xs font-semibold select-none"
        title={user ? `${user.firstName} ${user.lastName}` : undefined}
      >
        {initials}
      </div>
    </>
  );
};

// ── Expanded sidebar content ───────────────────────────────────────────

const ExpandedSidebarContent: React.FC = () => {
  const { toggleSidebar, closeSidebar, isMobile } = useChatLayout();
  const { user, logout, isPlatformAdmin } = useAuth();
  const hasPulse = useHasFeature('pulse');
  const location = useLocation();
  const navigate = useNavigate();

  const navItemsToShow = visibleNavItems({
    isInternalTester: user?.isInternalTester,
    hasPulse,
    isPlatformAdmin,
  });
  const activeHref = activeNavHref(location.pathname, navItemsToShow);

  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : '?';

  const linkBase =
    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors';
  const linkDefault =
    'text-muted-foreground hover:bg-accent';
  const linkActive =
    'bg-accent text-primary';

  const handleNavClick = (): void => {
    if (isMobile) closeSidebar();
  };

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <button
          onClick={() => navigate('/')}
          className="flex items-center hover:opacity-80 transition-opacity cursor-pointer"
          title="Go to home page"
        >
          <img
            src={zaminoLogo}
            alt="Zamino"
            className="h-8 w-auto"
          />
        </button>
        <button
          onClick={isMobile ? closeSidebar : toggleSidebar}
          className="flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
          title={isMobile ? 'Close sidebar' : 'Collapse sidebar (Ctrl+B)'}
        >
          {isMobile ? <X className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Nav links */}
      <nav className="px-3 mt-2 space-y-0.5">
        {navItemsToShow.map(({ icon: Icon, label, href }) => {
          const isActive = href === activeHref;
          return (
            <NavLink
              key={href}
              to={href}
              onClick={handleNavClick}
              className={clsx(linkBase, isActive ? linkActive : linkDefault)}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {label}
            </NavLink>
          );
        })}
      </nav>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Settings */}
      <nav className="px-3 mb-2">
        <NavLink
          to="/settings"
          onClick={handleNavClick}
          className={clsx(
            linkBase,
            location.pathname.startsWith('/settings') ? linkActive : linkDefault
          )}
        >
          <Settings className="w-4 h-4 flex-shrink-0" />
          Settings
        </NavLink>
      </nav>

      {/* User section */}
      <div className="px-3 py-3 border-t border-border">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-xs font-semibold select-none flex-shrink-0">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground truncate">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {user?.email}
            </p>
          </div>
          <button
            onClick={logout}
            className="flex items-center justify-center w-8 h-8 rounded-lg text-destructive hover:bg-destructive/15 transition-colors flex-shrink-0"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );
};

// ── Mobile drawer ──────────────────────────────────────────────────────

const MobileDrawer: React.FC = () => {
  const { sidebarExpanded, closeSidebar } = useChatLayout();

  return (
    <Transition show={sidebarExpanded} as={Fragment}>
      <Dialog onClose={closeSidebar} className="relative z-40">
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-out duration-250"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </Transition.Child>

        {/* Drawer panel */}
        <Transition.Child
          as={Fragment}
          enter="transition ease-out duration-250"
          enterFrom="-translate-x-full"
          enterTo="translate-x-0"
          leave="transition ease-in duration-200"
          leaveFrom="translate-x-0"
          leaveTo="-translate-x-full"
        >
          <Dialog.Panel className="fixed inset-y-0 left-0 w-[280px] flex flex-col bg-muted shadow-xl">
            <ExpandedSidebarContent />
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};

// ── Main ChatSidebar ────────────────────────────────────────────────────

const ChatSidebar: React.FC = () => {
  const { sidebarExpanded, isMobile } = useChatLayout();

  // On mobile, render the drawer overlay instead of the inline sidebar
  if (isMobile) {
    return <MobileDrawer />;
  }

  // Desktop: inline sidebar with expand/collapse
  return (
    <aside
      className={clsx(
        'flex-shrink-0 h-screen sticky top-0 flex flex-col border-r border-border bg-muted transition-[width] duration-200 ease-in-out overflow-hidden',
        sidebarExpanded ? 'w-[280px]' : 'w-14'
      )}
    >
      {sidebarExpanded ? (
        <div className="flex flex-col h-full" style={{ animation: 'sidebar-content-in 200ms ease-out' }}>
          <ExpandedSidebarContent />
        </div>
      ) : (
        <div className="flex flex-col items-center py-3 gap-1 h-full">
          <CollapsedSidebar />
        </div>
      )}
    </aside>
  );
};

export default ChatSidebar;
